<template>
    <v-card class="auth-card reset-card mx-auto" max-width="1240">
        <div class="auth-card__left">
            <div class="auth-brand">
                <img id="logo-image" alt="Smartdata logo image and dashboard link."
                            rel="noreferrer" class="mx-auto px-5" src="/static/img/logos/Smartdata_dark_L.png">
            </div>

            <div class="auth-form">
                <h1 class="auth-title">Reset your password</h1>
                <p class="auth-subtitle">
                    Enter your email and we will send a password reset link.
                </p>

                <v-alert
                    v-if="response"
                    variant="outlined"
                    :type="requestSuccessful ? 'success' : 'error'"
                    class="mb-4"
                >
                    {{ message }}
                </v-alert>

                <v-form v-if="!response || !requestSuccessful" class="full-width">
                    <v-text-field
                        v-model="email"
                        label="Email Address"
                        required
                        variant="outlined"
                        prepend-inner-icon="$vuetify.icons.email"
                        class="auth-input"
                        type="email"
                        name="email"
                        autocomplete="email"
                    />

                    <v-btn
                        size="large"
                        color="primary"
                        class="auth-submit mt-2"
                        @click="requestPasswordReset"
                    >
                        Request Password Reset
                        <v-icon end>$vuetify.icons.next</v-icon>
                    </v-btn>
                </v-form>
            </div>
        </div>

        <div class="auth-card__right">
            <div class="auth-illustration-wrap">
                <img
                    src="/static/img/smartdata-mockup-transparent.png"
                    alt="Smartdata platform illustration"
                    class="auth-illustration"
                />
            </div>
        </div>
    </v-card>
</template>

<script>
import axios from 'axios';
import { trackProfileEvents } from 'js/eventsTracking';

export default {
    name: 'PasswordResetForm',
    props: {
        userEmail: {
            type: String,
            default: null,
        },
        dark: {
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            email: null,
            requestSuccessful: false,
            response: false,
            message: null,
        };
    },
    mounted() {
        if (this.userEmail) {
            this.email = this.userEmail;
        }
    },
    methods: {
        requestPasswordReset() {
            this.response = false;
            axios
                .post(
                    `/accounts/password/reset/request/`,
                    JSON.stringify({ email: this.email })
                )
                .then(response => response.data)
                .then(data => {
                    trackProfileEvents.requestedPasswordReset();
                    this.response = true;
                    this.requestSuccessful = data.success;
                    this.message = data.message;
                });
        },
    },
};
</script>

<style scoped>
.auth-card {
    display: grid;
    grid-template-columns: 1fr 1.15fr;
    border-radius: 22px !important;
    overflow: hidden;
    box-shadow: 0 24px 48px rgba(20, 29, 71, 0.14) !important;
}

.auth-card__left {
    background: #f6f8fc;
    padding: 42px 52px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.auth-brand {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: #1ba0e6;
    margin-bottom: 20px;
}

.auth-brand__mark {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 700;
    background: #1ba0e6;
}

.auth-brand__name {
    font-size: 1.55rem;
    font-weight: 600;
    color: #1092d5;
}

.auth-form {
    max-width: 430px;
    width: 100%;
}

.auth-title {
    font-size: clamp(2rem, 2.8vw, 3rem);
    line-height: 1.08;
    margin-bottom: 6px;
    color: #1e2564;
}

.auth-subtitle {
    color: #55608f;
    font-size: 1.1rem;
    margin-bottom: 24px;
}

.auth-input {
    margin-bottom: 10px;
}

.auth-input :deep(.v-field) {
    background: #ffffff;
    border-radius: 12px;
}

.auth-submit {
    min-width: 240px;
    height: 50px;
    border-radius: 14px !important;
    letter-spacing: 0.01em;
    text-transform: none;
    font-size: 1.02rem !important;
}

.auth-card__right {
    position: relative;
    background: linear-gradient(160deg, #4e4fe7 0%, #38a9e6 55%, #37b6d8 100%);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding: 30px 20px 0;
}

.auth-card__right::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
        radial-gradient(circle at 30% 20%, rgba(255, 255, 255, 0.25), transparent 42%),
        linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0));
}

.auth-illustration-wrap {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: flex-end;
    justify-content: center;
}

.auth-illustration {
    width: min(100%, 760px);
    max-height: 100%;
    object-fit: contain;
    transform: translateX(-4%);
    filter: drop-shadow(0 14px 20px rgba(8, 24, 72, 0.3));
}

@media (max-width: 1100px) {
    .auth-card {
        grid-template-columns: 1fr;
    }

    .auth-card__right {
        min-height: 280px;
        padding-top: 12px;
    }

    .auth-illustration {
        transform: none;
        width: min(92%, 560px);
    }
}

@media (max-width: 700px) {
    .auth-card__left {
        padding: 28px 22px;
    }

    .auth-brand {
        margin-bottom: 8px;
    }

    .auth-card__right {
        min-height: 220px;
    }
}
</style>
